import { alpha, createTheme } from '@mui/material/styles';

export const THEME_MODE_STORAGE_KEY = 'themeMode';

const lightPalette = {
  mode: 'light',
  divider: '#e6e8ec',
  text: { primary: '#1f2937', secondary: '#667085', disabled: '#98a2b3' },
  focus: { main: '#2563eb' },
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
  },
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
  },
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
  },
  incoming: {
    main: '#dfc968',
    light: '#fbbf24',
    dark: '#94881a',
  },
  overdue: {
    main: '#c62828',
    light: '#e57373',
    dark: '#8e0000',
  },
  chart: {
    successfulResults: '#4f9b7bff',
    unsuccessfulResults: '#d32f2f',
  },
  fieldType: {
    boolean: '#2e7d32',
    number: '#0288d1',
    text: '#b07400',
    select: '#7e57c2',
    radio: '#2e7d32',
    unchecked: '#8a8a8a',
  },
  primary: {
    main: '#287655',
    contrastText: '#FFFFFF',
    white: '#FFFFFF',
    black: '#000000',
    gray: '#fafbfc',
  },
  secondary: {
    main: '#475467',
  },
  logo: {
    main: '#4f9b7bff',
  },
  background: {
    default: '#f5f6f7',
    paper: '#FFFFFF',
  },
  link: {
    main: '#287655',
    menu: '#1f2937',
  },
  action: {
    hover: '#f3f4f6',
    hoverDark: '#000',
    lightHover: '#f7f7f7f3',
  },
  menu: {
    selected: '#ecf4f0',
    selectedSpan: '#4f9b7bff',
  },
  custom: {
    darkGreen: '#18221d',
    lightGreen: '#5f6f66',
    whiteGreen: '#dfe8e3',
    shadow: '#abb4b0',
  },
  notification: {
    space: '#7e57c2',
    inspection: '#1976d2',
    dueSoon: '#f9a825',
    overdue: '#d32f2f',
    limitReached: '#ef6c00',
    unreadDot: '#4f9b7bff',
    unreadBackground: '#d1ebdc',
  },
};

const darkPalette = {
  mode: 'dark',
  focus: { main: '#93c5fd' },
  divider: '#2b3b32',
  text: { primary: '#e6eee9', secondary: '#a5b7ac', disabled: '#748b7e' },
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
  },
  warning: {
    main: '#ffa726',
    light: '#ffb74d',
    dark: '#f57c00',
  },
  info: {
    main: '#29b6f6',
    light: '#4fc3f7',
    dark: '#0288d1',
  },
  success: {
    main: '#66bb6a',
    light: '#81c784',
    dark: '#388e3c',
  },
  incoming: {
    main: '#fbbf24',
    light: '#fde047',
    dark: '#d97706',
  },
  overdue: {
    main: '#e57373',
    light: '#ef9a9a',
    dark: '#c62828',
  },
  chart: {
    successfulResults: '#6fbfa0',
    unsuccessfulResults: '#ef5350',
  },
  fieldType: {
    boolean: '#66bb6a',
    number: '#29b6f6',
    text: '#ffb74d',
    select: '#b39ddb',
    radio: '#66bb6a',
    unchecked: '#9e9e9e',
  },
  primary: {
    main: '#6fbfa0',
    contrastText: '#10251b',
    white: '#192720',
    black: '#FFFFFF',
    gray: '#22332a',
  },
  secondary: {
    main: '#FFFFFF',
  },
  logo: {
    main: '#6fbfa0',
  },
  background: {
    default: '#111c17',
    paper: '#192720',
  },
  link: {
    main: '#6fbfa0',
    menu: '#FFFFFF',
  },
  action: {
    hover: '#22332a',
    hoverDark: '#FFFFFF',
    lightHover: '#1a1a1a',
  },
  menu: {
    selected: '#2a3a33',
    selectedSpan: '#6fbfa0',
  },
  custom: {
    darkGreen: '#dfe8e3',
    lightGreen: '#a8b8b0',
    whiteGreen: '#2a3a33',
    shadow: '#000000',
  },
  notification: {
    space: '#b39ddb',
    inspection: '#64b5f6',
    dueSoon: '#ffd54f',
    overdue: '#ef5350',
    limitReached: '#ffa726',
    unreadDot: '#6fbfa0',
    unreadBackground: '#1f2a25',
  },
};

export const createAppTheme = (mode) => {
  const isDark = mode === 'dark';
  const palette = isDark ? darkPalette : lightPalette;
  const focusRing = { outline: `2px solid ${palette.focus.main}`, outlineOffset: 3 };
  const inputBackground = isDark ? '#22332a' : '#FFFFFF';

  const shadow = {
    card: 'none',
    popover: isDark
      ? '0 8px 24px rgba(0,0,0,0.6), 0 2px 6px rgba(0,0,0,0.5)'
      : '0 8px 24px rgba(16,24,40,0.12), 0 2px 6px rgba(16,24,40,0.08)',
  };

  return createTheme({
    typography: {
      fontFamily: ['Noto Sans', 'Arial', 'sans-serif'].join(','),
      fontSize: 14,
      fontSizeMedium: 16,
      fontSizeTitle: 22,
      fontSizeSection: 18,
      fontSizeMetric: 36,
      fontSizeBig: 26,
      fontSizeVeryBig: 40,
      fontSizeSmall: 12,
      lineHeight: 1.6,
      h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.035em' },
      h2: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.025em' },
      h3: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h4: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.015em' },
      h5: { fontSize: '1.125rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600 },
      body1: { lineHeight: 1.65 },
      body2: { lineHeight: 1.6 },
      button: { fontWeight: 600, letterSpacing: 0, textTransform: 'none' },
    },
    shadow,
    palette,
    padding: {
      small: '5px',
      content: '10px',
      container: '70px',
      containerBig: '50px',
      section: '20px',
      footer: '20px',
      sideMenuButton: '65px',
      sideMenuButtonHidden: '30px',
    },
    radius: {
      card: '12px',
    },
    height: {
      topMenu: '60px',
      footer: '100px',
      notificationDropdown: '480px',
    },
    width: {
      sideMenu: '300px',
      sideMenuLeft: '270px',
      filterDrawer: '450px',
      form: '600px',
      formMin: '300px',
      topMenuLogo: '40px',
      descriptionItem: '300px',
      limitedSpan: '200px',
      notificationDropdown: '380px',
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { colorScheme: palette.mode },
          '::selection': {
            backgroundColor: isDark ? alpha(palette.primary.main, 0.22) : alpha('#3b82f6', 0.18),
          },
          ':focus-visible': focusRing,
        },
      },
      MuiButtonBase: {
        styleOverrides: { root: { '&.Mui-focusVisible': focusRing } },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            border: `1px solid ${palette.divider}`,
            boxShadow: shadow.popover,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: { root: { borderRadius: 6, margin: '2px 6px' } },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            '&.MuiContainer-maxWidthXl': {
              maxWidth: '1450px',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            minHeight: 44,
            padding: '8px 16px',
            lineHeight: 1.5,
            '&.MuiButton-sizeSmall': { minHeight: 36, padding: '6px 10px' },
            '&.MuiButton-sizeLarge': { minHeight: 48, padding: '12px 22px' },
            '& .MuiButton-startIcon > *, & .MuiButton-endIcon > *': { fontSize: 20 },
          },
          outlined: ({ ownerState }) =>
            ['primary', 'secondary'].includes(ownerState.color)
              ? {
                  color: palette.text.primary,
                  borderColor: isDark ? palette.divider : '#d0d5dd',
                  '&:hover': {
                    borderColor: palette.text.secondary,
                    backgroundColor: palette.action.hover,
                  },
                }
              : {},
          contained: {
            '& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root': {
              color: 'inherit',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(ownerState.color === 'default' && { color: palette.text.secondary }),
            '& .MuiSvgIcon-root': { fontSize: 20 },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
        },
        variants: [
          {
            props: { color: 'overdue' },
            style: ({ theme }) => ({
              backgroundColor: theme.palette.overdue.main,
              color: theme.palette.getContrastText(theme.palette.overdue.main),
            }),
          },
          {
            props: { color: 'incoming' },
            style: ({ theme }) => ({
              backgroundColor: theme.palette.incoming.main,
              color: theme.palette.getContrastText(theme.palette.incoming.main),
            }),
          },
        ],
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: { color: palette.text.secondary, '& .MuiSvgIcon-root': { fontSize: 20 } },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { lineHeight: 1.5, marginTop: 6 } },
      },
      MuiInputLabel: {
        styleOverrides: {
          outlined: ({ theme }) => ({
            '&.Mui-focused:not(.Mui-error)': { color: palette.focus.main },
            '&.MuiInputLabel-shrink': {
              [theme.breakpoints.down('lg')]: {
                backgroundColor: inputBackground,
                paddingLeft: 4,
                paddingRight: 4,
                zIndex: 1,
              },
            },
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            boxShadow: shadow.popover,
            border: `1px solid ${palette.divider}`,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: inputBackground,
            transition: 'box-shadow 160ms ease, border-color 160ms ease',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#50665a' : '#8793a3' },
            '&:hover:not(.Mui-disabled):not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline':
              { borderColor: palette.text.secondary },
            '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.focus.main,
              borderWidth: 1,
            },
            '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(palette.focus.main, 0.14)}` },
            '&.Mui-disabled': { backgroundColor: palette.action.hover },
            '&.Mui-error.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(palette.error.main, 0.14)}`,
            },
          },
        },
      },
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: inputBackground,
            transition: 'box-shadow 160ms ease',
            '& .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#50665a' : '#8793a3',
            },
            '&:hover:not(.Mui-disabled):not(.Mui-error):not(.Mui-focused) .MuiPickersOutlinedInput-notchedOutline':
              { borderColor: palette.text.secondary },
            '&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: palette.focus.main,
              borderWidth: 1,
            },
            '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(palette.focus.main, 0.14)}` },
            '&.Mui-error.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(palette.error.main, 0.14)}`,
            },
            '&.Mui-disabled': { backgroundColor: palette.action.hover },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: inputBackground,
            '&:hover': {
              backgroundColor: inputBackground,
            },
            '&.Mui-focused': {
              backgroundColor: inputBackground,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: { '&::placeholder': { color: palette.text.secondary, opacity: 1 } },
          root: {
            backgroundColor: inputBackground,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            fontSize: theme.typography.fontSize,
          }),
        },
      },
    },
  });
};

const getStoredThemeMode = () => {
  const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
  return stored === 'dark' ? 'dark' : 'light';
};

const theme = createAppTheme(getStoredThemeMode());

export default theme;
